import React, { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { useDialogs } from "../../../../../providers/dialogs_provider";
import { useFinishes } from "../../../../../providers/finish_provider";

interface Props {
  coloreTelaioNsRal: string;
  coloreTelaioRal: string;
  setFieldValue: (field: string, value: any) => void;
}

type TipoColoreTelaio = "ral_standard" | "ral_non_standard";

export const FrmColorTelaio: React.FC<Props> = ({
  coloreTelaioNsRal,
  coloreTelaioRal,
  setFieldValue
}) => {
  const dialogs = useDialogs();
  const finishesProvider = useFinishes();
  const standardColorCodes = ["9016", "7016"];
  const getTipoColoreTelaio = (): TipoColoreTelaio =>
    coloreTelaioNsRal && !standardColorCodes.includes(coloreTelaioNsRal) ? "ral_non_standard" : "ral_standard";

  const [tipoColoreTelaio, setTipoColoreTelaio] = useState<TipoColoreTelaio>(getTipoColoreTelaio());
  const [previews, setPreviews] = useState<Record<string, string>>({});

  useEffect(() => {
    setTipoColoreTelaio(getTipoColoreTelaio());
  }, [coloreTelaioNsRal]);

  useEffect(() => {
    const loadPreviews = async () => {
      const entries = await Promise.all(
        standardColorCodes.map(async (code) => {
          const img = await finishesProvider?.getImgPreview(code);
          return [code, img?.src ?? ""] as [string, string];
        })
      );
      setPreviews(Object.fromEntries(entries));
    };
    loadPreviews();
  }, [finishesProvider]);

  return (
    <>
      <FormControl fullWidth className="mt-4">
        <InputLabel id="tipo_colore_telaio">Colore Telaio</InputLabel>
        <Select
          labelId="tipo_colore_telaio"
          value={tipoColoreTelaio}
          label="Colore Telaio"
          onChange={(e) => {
            const selected = e.target.value as TipoColoreTelaio;
            setTipoColoreTelaio(selected);
            if (selected === "ral_standard") {
              setFieldValue("coloreTelaioNsRal", standardColorCodes.includes(coloreTelaioNsRal) ? coloreTelaioNsRal : standardColorCodes[0]);
            } else {
              setFieldValue("coloreTelaioNsRal", "");
            }
          }}
        >
          <MenuItem value={"ral_standard"}>RAL Standard</MenuItem>
          <MenuItem value={"ral_non_standard"}>RAL NON Standard</MenuItem>
        </Select>
      </FormControl>

      {tipoColoreTelaio === "ral_standard" && (
        <FormControl fullWidth className="mt-2">
          <InputLabel id="colore_telaio_standard">Seleziona il colore</InputLabel>
          <Select
            labelId="colore_telaio_standard"
            value={standardColorCodes.includes(coloreTelaioNsRal) ? coloreTelaioNsRal : ""}
            label="Seleziona il colore"
            onChange={(e) => setFieldValue("coloreTelaioNsRal", e.target.value)}
          >
            {standardColorCodes.map((c) => (
              <MenuItem key={c} value={c}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  {finishesProvider?.getCodeName(c)}
                  {previews[c] && (
                    <img src={previews[c]} alt={c} style={{ width: "2rem", height: "1.75rem", marginLeft: "1rem" }} />
                  )}
                </div>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {tipoColoreTelaio === "ral_non_standard" && (
        <TextField
          fullWidth
          className="mt-2"
          label={!coloreTelaioNsRal ? "Seleziona il colore" : ""}
          name="coloreTelaioNsRal"
          value={finishesProvider?.getRalName(coloreTelaioNsRal)}
          InputProps={{
            readOnly: true,
            startAdornment: coloreTelaioNsRal && (
              <InputAdornment position="start">
                {finishesProvider?.getRalPreview(coloreTelaioNsRal)}
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() =>
                    dialogs?.selectRalColor({
                      skipRals: standardColorCodes,
                      onConfirm: (selectedColor) => {
                        setFieldValue("coloreTelaioRal", 0);
                        setFieldValue("coloreTelaioNsRal", selectedColor);
                      }
                    })
                  }
                >
                  <OpenInNewIcon style={{ color: "black" }} />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      )}
    </>
  );
};
